import { useEffect, useRef, useState } from "react";
import type { PaletteLike } from "@picnic-field/renderer/appearance";
import type { GalleryItem, Sticker } from "../../../lib/api/socialApi";
import { Button } from "../../../components/ui/Button";
import { ModelPreview } from "../../../components/ModelPreview";
import { TagChips } from "../../../components/ui/TagChips";
import { useDensity } from "../../../store/density";
import { useI18n } from "../../../i18n";

/**
 * FS-03-02 작품 카드 — SCR v0.6 sfs-03(SE) · sfs-10-su '작품 카드(SU · FS-03-02 변형)'.
 * 구성: 썸네일(Campus 색 3D) · 작품명(차시 + 별명) · 원리 태그 칩 · 작동(60초) · 데려오기 · 막힘 배너 · 스티커 · 내 반응.
 * 상태: 작동 중(타이머) · 작동 중 막힘(빨간 포트 '막히면 멈춰요') · 내 작품(스티커 불가 — 붙은 개수는 나에게만) · 반응 완료.
 * 반응 개수는 남의 카드에 그리지 않는다(SR-SOC-02). 스티커는 카탈로그 키만(자유 입력 0 — DR-02).
 */
export interface ExhibitCardProps {
  item: GalleryItem;
  title: string;
  palette: PaletteLike | null;
  stickers: readonly Sticker[];
  myReaction: string | null;
  /** 작동 중이면 남은 초 */
  activeS: number | null;
  stalled: readonly string[];
  /** 친구 자리에 이미 있음 */
  brought: boolean;
  canBring: boolean;
  onActivate: () => void;
  /** 작동 중 멈추기(받침대로) */
  onDeactivate: () => void;
  /** 프로그램 움직임 판정(lib/programMotion) — remoteOnly · still 이면 '전시관에서는 가만히 있어요' 안내 */
  motion?: "relay" | "moves" | "remoteOnly" | "still" | "unknown";
  onBring: () => void;
  onSticker: (key: string) => void;
  onClearSticker: () => void;
  onClose: () => void;
}

/** 줄에 바로 보이는 스티커 수(와이어 3개) — 나머지는 '＋' 팝오버 */
const INLINE = 3;

export function ExhibitCard(p: ExhibitCardProps) {
  const { t } = useI18n();
  const { density } = useDensity();
  const se = density === "se";
  const { item } = p;
  const [more, setMore] = useState(false);
  const headRef = useRef<HTMLHeadingElement>(null);
  useEffect(() => headRef.current?.focus(), [item.snapshotId]);
  useEffect(() => setMore(false), [item.snapshotId]);
  const mine = p.stickers.find((s) => s.key === p.myReaction) ?? null;
  const active = p.activeS != null;
  const port = p.stalled[0] ?? null;

  const sticker = (s: Sticker) => {
    const on = s.key === p.myReaction;
    return (
      <Button
        key={s.key}
        variant={on ? "accent" : "ghost"}
        className="pf-card3__sticker"
        aria-pressed={on}
        aria-label={t(on ? "gallery.card.stickerRemove" : "gallery.card.stickerPut", {
          name: s.name,
        })}
        data-sticker={s.key}
        onClick={() => {
          setMore(false);
          p.onSticker(s.key);
        }}
      >
        <span aria-hidden="true">{s.emoji}</span>
      </Button>
    );
  };

  return (
    <div
      className={`pf-pane pf-pane--hero pf-card3 pf-card3--${density}`}
      role="dialog"
      aria-labelledby="card3-title"
      data-testid="fs-03-02"
      data-screen="FS-03-02"
      data-owner={item.snapshotId}
      data-mine={item.mine}
      onKeyDown={(e) => {
        if (e.key === "Escape") {
          e.stopPropagation();
          if (more) setMore(false);
          else p.onClose();
        }
      }}
    >
      <button
        type="button"
        className="pf-card3__close"
        aria-label={t("common.close")}
        onClick={p.onClose}
        data-testid="card-close"
      >
        <span aria-hidden="true">✕</span>
      </button>
      <div className="pf-card3__head">
        <ModelPreview
          className="pf-card3__thumb"
          glbUrl={item.glbUrl}
          appearance={item.appearance}
          palette={p.palette}
          label={t("student.work.thumbLabel", { title: p.title })}
          interactive={false}
          autoRotate={false}
          quality="low"
        />
        <div className="pf-card3__who">
          <h2
            id="card3-title"
            ref={headRef}
            tabIndex={-1}
            className="pf-card3__title"
            data-testid="card-title"
          >
            {p.title}
          </h2>
          <TagChips tags={item.tags} label={t("student.work.tags")} />
          {item.static && (
            <ul className="pf-chips">
              <li className="pf-chip-static pf-chip-static--run" data-testid="card-static">
                {t("student.work.static")}
              </li>
            </ul>
          )}
        </div>
      </div>
      {!item.static && (p.motion === "remoteOnly" || p.motion === "still") && (
        <p className="pf-card3__note" data-testid="card-motion-note" data-motion={p.motion}>
          {t(p.motion === "remoteOnly" ? "gallery.card.remoteOnly" : "gallery.card.still")}
        </p>
      )}
      <div className="pf-card3__actions">
        {!item.static && (
          <Button
            variant={se ? "accent" : "green"}
            icon={se ? (active ? "⏹" : "▶") : undefined}
            className={`pf-card3__run${se ? "" : " pf-btn--sm"}`}
            onClick={active ? p.onDeactivate : p.onActivate}
            data-testid="card-activate"
            data-active={active}
          >
            {active
              ? t(se ? "gallery.card.stopSE" : "gallery.card.stopSU", { s: p.activeS ?? 0 })
              : t(se ? "gallery.card.runSE" : "gallery.card.runSU")}
          </Button>
        )}
        {!item.mine && (
          <Button
            variant="ghost"
            icon={se ? "⬇" : undefined}
            className={se ? undefined : "pf-btn--sm"}
            onClick={p.onBring}
            disabled={p.brought || !p.canBring}
            aria-pressed={p.brought}
            data-testid="card-bring"
          >
            {t(p.brought ? "gallery.card.brought" : "gallery.card.bring")}
          </Button>
        )}
        {!se && (
          <span className="pf-muted pf-card3__mine" data-testid="card-my-reaction">
            {item.mine
              ? t("gallery.card.countOnMine", { n: item.reactionCount ?? 0 })
              : t("gallery.card.mySU", { emoji: mine?.emoji ?? "—" })}
          </span>
        )}
      </div>
      {active && port && (
        <div
          className="pf-banner pf-banner--err pf-card3__stall"
          role="status"
          data-testid="card-stall"
          data-port={port}
        >
          <p className="pf-banner__text">
            {t(se ? "gallery.card.stallSE" : "gallery.card.stallSU", { port })}
          </p>
        </div>
      )}
      {item.mine ? (
        se && (
          <p className="pf-muted pf-card3__row" data-testid="card-my-reaction">
            {t("gallery.card.countOnMine", { n: item.reactionCount ?? 0 })}
          </p>
        )
      ) : (
        <div
          className="pf-card3__row"
          role="group"
          aria-label={t("gallery.card.stickers")}
          data-testid="card-stickers"
        >
          <span className="pf-muted">{t("gallery.card.stickerLabel")}</span>
          {p.stickers.slice(0, INLINE).map(sticker)}
          {p.stickers.length > INLINE && (
            <Button
              variant="ghost"
              className="pf-card3__sticker"
              aria-label={t("gallery.card.moreStickers")}
              aria-expanded={more}
              aria-controls="card3-stickers"
              onClick={() => setMore((m) => !m)}
              data-testid="sticker-more"
            >
              ＋
            </Button>
          )}
          {se && (
            <span className="pf-muted pf-card3__mine" data-testid="card-my-reaction">
              {t("gallery.card.mySE", { emoji: mine?.emoji ?? "—" })}
            </span>
          )}
        </div>
      )}
      {more && !item.mine && (
        <div
          className="pf-pane pf-card3__pop"
          id="card3-stickers"
          role="dialog"
          aria-label={t("gallery.card.stickers")}
          data-testid="sticker-popover"
        >
          <div className="pf-card3__pop-grid">{p.stickers.map(sticker)}</div>
          {mine && (
            <Button
              variant="ghost"
              icon="✕"
              className="pf-btn--sm"
              onClick={() => {
                setMore(false);
                p.onClearSticker();
              }}
              data-testid="sticker-clear"
            >
              {t("gallery.card.stickerClear")}
            </Button>
          )}
        </div>
      )}
    </div>
  );
}
